import { shiftDate, storeDate } from "@estelle/domain";
import type { IsoDate, Money, Order } from "@estelle/domain";

import { sumMoney } from "@/modules/gst/format";

/** One day's revenue, oldest first. */
export interface RevenueDay {
  /** The store's calendar day, `YYYY-MM-DD`. */
  date: IsoDate;
  orderCount: number;
  revenue: Money;
}

/**
 * Buckets orders into the store's calendar days, the last `days` of them with
 * the day `now` falls on the newest, oldest first.
 *
 * Calendar days, in the store's timezone, not rolling 24-hour windows back
 * from `now`. Every bar is read off a tooltip as "Mon, 5 Oct 2026", and a bar
 * of the 5th that held the afternoon of the 4th would be a date that is
 * wrong. It is also the day "Orders today" and the Orders report mean, so the
 * newest bar and the tile beside it count the same orders.
 *
 * `now` is a parameter rather than reading `FIXTURE_NOW` in here, so this
 * stays a pure function a real API's response could run through unchanged —
 * only the caller knows which clock is live.
 *
 * Money is summed as integer paise inside `sumMoney`, not as floats — the
 * same reason `modules/gst/format.ts` does it for the HSN summary. A chart
 * only needs the result to be close, but the total this function's own
 * caller prints beside the chart has to reconcile with it exactly, and a
 * second summing routine that could disagree with `sumMoney` is worse than
 * reusing the one that is already trusted.
 */
export function aggregateRevenueByDay(
  orders: readonly Order[],
  days: number,
  now: number,
): RevenueDay[] {
  const today = storeDate(now);
  const dates = Array.from({ length: days }, (_, index) =>
    shiftDate(today, index + 1 - days),
  );
  const buckets = new Map<IsoDate, Order[]>(dates.map((date) => [date, []]));

  for (const order of orders) {
    if (Number.isNaN(Date.parse(order.placedAt))) continue;
    // Older than the window, or placed after today — a caller who over-fetched
    // must not corrupt the oldest or newest bucket with a stray row.
    buckets.get(storeDate(order.placedAt))?.push(order);
  }

  return dates.map((date) => {
    const bucket = buckets.get(date) ?? [];
    return {
      date,
      orderCount: bucket.length,
      revenue: sumMoney(bucket.map((order) => order.total)),
    };
  });
}

/** `1 order`, `4 orders` — what a bar's tooltip says under its figure. */
export function orderCountNote(count: number): string {
  return `${new Intl.NumberFormat("en-IN").format(count)} ${count === 1 ? "order" : "orders"}`;
}
